import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "@app/ui/lib/Utils";

export interface TooltipProps {
  content: React.ReactNode;
  /** 트리거 요소 하나. hover 와 키보드 focus 양쪽에서 떠요. */
  children: React.ReactElement;
  /** 툴팁이 뜨는 방향. 기본은 아래(bottom). 예: 사이드바 네비는 오른쪽(right). */
  side?: React.ComponentProps<typeof PopoverPrimitive.Content>["side"];
  className?: string;
}

/**
 * hover/focus/터치 로 뜨는 안내 툴팁. Dropdown 과 같은 @radix-ui/react-popover +
 * Portal 조합을 써서 body 최상단에 렌더링돼요 — 모달의 `overflow-y-auto` 경계
 * 안에 있는 absolute 요소처럼 잘리지 않아요.
 *
 * 앵커는 `Trigger` 가 아니라 `Anchor` 예요. `Trigger` 는 클릭(탭)마다 open 을
 * 토글하는데, 터치 첫 탭에서는 focus(→open)와 click(→토글로 close)이 겹쳐
 * 툴팁이 떴다가 곧바로 사라졌어요. 토글 없는 `Anchor` 로 두고 열기는 아래
 * 핸들러(hover/focus/click)로만 제어해, 탭 한 번에 확실히 뜨게 해요. 닫기는
 * mouseleave/blur 와 radix 의 바깥-탭 dismiss 가 맡아요.
 */
const Tooltip = ({ content, children, side = "bottom", className }: TooltipProps) => {
  const [open, setOpen] = React.useState(false);

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Anchor
        asChild
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        // iOS Safari 는 탭해도 버튼에 focus 가 안 붙어 onFocus 만으로는 안 떠요.
        // 클릭(탭)으로도 확실히 열어요 — 토글이 아니라 항상 열기라 데스크톱
        // 클릭에서도 열린 뒤 mouseleave 로 닫혀요.
        onClick={() => setOpen(true)}
      >
        {children}
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          role="tooltip"
          side={side}
          sideOffset={6}
          collisionPadding={8}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          className={cn(
            "z-50 max-w-64 pointer-events-none rounded-md bg-foreground px-2 py-1 text-center text-xs text-background shadow-md duration-150 fill-mode-forwards data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
            className,
          )}
        >
          {content}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
};
Tooltip.displayName = "Tooltip";

export { Tooltip };
